import React from "react";
import styled from "styled-components";
import dayjs from "dayjs";

import * as UserContext from "../../components/UserContext";
import * as gtag from "../../utils/gtag";
import * as u from "../../utils";
import CONTENT_TAGS from "../../utils/content-tags";
import * as T from "../../components/Typography";
import * as I from "../../components/Icons";
import Link from "../../components/Link";
import UpsellModal from "../../components/UpsellModal";
import Search from "../../components/Search";
import DropdownFilter from "../../components/DropdownFilter";
import { VerticalDivider } from "../../components/Utils";
import PostFeaturedContent from "@/components/post/PostFeaturedContent";
import PostCarouselContent from "@/components/post/PostCarouselContent";
import PostArticleCard from "@/components/post/PostArticleCard";
import _ from "lodash";

export default function Edit(props) {
  const allArticles = _.orderBy(
    [
      props.feature,
      ...(props?.more || null),
      ...(props?.sponsored || null),
      ...(props?.subFeatures || null),
      ...(props?.trending || null),
    ].filter((value) => value !== null),
    "firstPublished",
    "desc"
  );

  const userInfo = React.useContext(UserContext.Context);
  const { hasLoaded } = userInfo;
  const [modalType, setModalType] = React.useState(false);

  const [selectedTags, setSelectedTags] = React.useState([]);
  const [searchText, setSearchText] = React.useState("");
  const [filteredArticles, setFilteredArticles] = React.useState(allArticles);

  React.useEffect(filterArticles, [selectedTags, searchText]);

  return (
    hasLoaded && (
      <S.Background>
        <S.Container data-cy="Edit__page">
          <UpsellModal type={modalType} setType={setModalType} />
          <S.SearchAndFiltersContainer>
            <S.Left></S.Left>
            <VerticalDivider />
            <DropdownFilter
              name="edit-topic"
              hint="Filter articles by topic"
              list={CONTENT_TAGS}
              value={selectedTags}
              onSelect={setSelectedTags}
            />
            <VerticalDivider />
            <Search
              onSearch={(searchValue) => setSearchText(searchValue)}
              name="academy"
            />
          </S.SearchAndFiltersContainer>
          <S.Logo data-cy="Edit__page--title">
            <TT.H5>The AllBright </TT.H5>
            <TT.H1S>
              Po<TT.Em>s</TT.Em>t
            </TT.H1S>
          </S.Logo>
          <FilteredResults />
        </S.Container>
      </S.Background>
    )
  );

  function FilteredResults() {
    if (selectedTags.length > 0 || searchText.length > 0) {
      if (filteredArticles.length === 0)
        return (
          <T.H4>
            Unfortunately, there are no articles matching your search. Please
            update your search criteria and try again.
          </T.H4>
        );
      return (
        <SM.Results>
          {filteredArticles.map((article) => (
            <PostArticleCard
              {...article}
              onClick={() => onClickArticle("More")}
              key={article.title}
            />
          ))}
        </SM.Results>
      );
    }

    return (
      <>
        {userMenu()}
        <PostFeaturedContent articles={allArticles.slice(0, 3)} />
        <PostCarouselContent
          articles={props["trending"]}
          title="Trending"
          name="trending"
        />
        <More />
      </>
    );
  }

  function userMenu() {
    return (
      <S.DropdownHover data-cy="Edit__menu">
        <S.DotsIcon />
        <TT.A isTop>Your AllBright Post</TT.A>
        <S.DropdownContent data-cy="Edit__menu--dropdown">
          <Link href="/edit/articles?saved">
            <S.MenuItem
              onClick={() => {
                triggerGA("Saved");
              }}
            >
              <I.Bookmark />
              <TT.A>Saved</TT.A>
            </S.MenuItem>
          </Link>
          <Link href="/edit/articles?following">
            <S.MenuItem
              onClick={() => {
                triggerGA("Following");
              }}
            >
              <I.Plus />
              <TT.A>Following</TT.A>
            </S.MenuItem>
          </Link>
          <Link href="/edit/articles?recommended">
            <S.MenuItem
              onClick={() => {
                triggerGA("Recommended");
              }}
            >
              <I.Heart />
              <TT.A>Recommended</TT.A>
            </S.MenuItem>
          </Link>
          <S.Line />
          {userInfo.authorId && (
            <Link href={`/edit/articles?author=${userInfo.authorId}`}>
              <S.MenuItem
                onClick={() => {
                  triggerGA("My articles");
                }}
              >
                <I.Write />
                <TT.A>My articles</TT.A>
              </S.MenuItem>
            </Link>
          )}
          <Link href="/edit/share-story">
            <S.MenuItem
              onClick={() => {
                triggerGA("Share story");
              }}
            >
              <I.Pencil />
              <TT.A>Share a story</TT.A>
            </S.MenuItem>
          </Link>
        </S.DropdownContent>
      </S.DropdownHover>
    );

    function triggerGA(page) {
      gtag.event({
        name: gtag.GA_EVENT.EDIT_ENGAGEMENT,
        category: "EDIT",
        label: "Page Link Click",
        value: page,
      });
    }
  }

  function More() {
    return (
      <S.More>
        <TT.H3>More from The AllBright Post</TT.H3>
        <SM.Explore>
          <SM.Results>
            {props.more.map((article) => (
              <PostArticleCard
                {...article}
                onClick={() => onClickArticle("More")}
                key={article.title}
              />
            ))}
          </SM.Results>
        </SM.Explore>
      </S.More>
    );
  }

  function onClickArticle(src) {
    gtag.event({
      name: gtag.GA_EVENT.EDIT_ENGAGEMENT,
      category: "EDIT",
      label: "From home",
      value: src,
    });
  }

  function filterArticles() {
    let unFilteredArticles = [...allArticles];

    if (selectedTags.length === 0 && searchText.length === 0) {
      setFilteredArticles(unFilteredArticles);
    } else {
      if (selectedTags.length > 0) {
        unFilteredArticles = unFilteredArticles.filter(tagFilter);
      }
      if (searchText.length > 0) {
        unFilteredArticles = unFilteredArticles.filter((article) =>
          article.title?.toLowerCase().includes(searchText.toLowerCase())
        );
      }
      setFilteredArticles(unFilteredArticles);
    }

    function tagFilter(article) {
      const articleTags = article.contentfulMetadata.tags.map(
        (tag) => tag.name.split(": ")[1]
      );
      const contentMatch = articleTags?.some((tag) =>
        selectedTags.includes(tag)
      );
      return contentMatch;
    }
  }
}

const LOCALES = {
  "en-au": "4smsC6nDGQrCqCpYvWhKDl",
  "en-gb": "5PpXpalx6TsCA6vx7DpgBo",
  "en-us": "5ch5kEJqGPDMpjFfdRBKT9",
};

export async function getStaticProps(ctx) {
  const axios = require("axios");
  const config = require("../../config");
  const firebase = require("../../firebase.admin");
  const db = firebase.firestore();
  const fbArticleData = (await db.collection("articles").get()).docs.map((d) =>
    d.data()
  );
  const fbAuthorData = (await db.collection("authors").get()).docs.map((d) =>
    d.data()
  );

  const { data } = (await axios.post(config.CONTENTFUL_EDIT, getGraphQuery()))
    .data;
  const props = getProps();

  return { props, revalidate: 3600 };

  function getProps() {
    const byNotLocalised = (article) =>
      [feature, ...subFeatures, ...sponsored].every(
        (a) => a.sys.id !== article.sys.id
      );
    const byPopular = (a, b) => b.score - a.score;
    const byLatest = (a, b) => b.firstPublished - a.firstPublished;
    const toCountTopics = (topics, article) => {
      return { ...topics, [article.topic]: (topics[article.topic] || 0) + 1 };
    };
    const byMostUsed = (a, b) => b[1] - a[1];

    const feature = addFbInfo(data.localisedContent.feature);
    const subFeatures =
      data.localisedContent.subFeaturesCollection.items.map(addFbInfo);
    const sponsored =
      data.localisedContent.sponsoredCollection.items.map(addFbInfo);
    const notLocalised = data.articleCollection.items
      .filter(byNotLocalised)
      .map(addFbInfo);
    const trending = notLocalised.sort(byPopular).slice(0, 4);
    const more = notLocalised.slice(4).sort(byLatest);
    const topics = data.articleCollection.items.reduce(toCountTopics, {});
    const popularTopics = Object.entries(topics)
      .sort(byMostUsed)
      .map((a) => a[0]);

    return {
      feature,
      subFeatures,
      trending,
      sponsored,
      more,
      popularTopics,
    };

    function addFbInfo(article) {
      const fbArticle = fbArticleData.find((a) => a.id === article.sys.id);
      const fbAuthor =
        fbAuthorData.find((a) => a.email === article.author) ||
        fbAuthorData.find((a) => a.email === "digital@allbright.co");
      const firstPublished =
        fbArticle?.firstPublished ||
        dayjs(article.sys.firstPublishedAt).unix() ||
        null;

      return { ...article, ...fbArticle, author: fbAuthor, firstPublished };
    }
  }

  function getGraphQuery() {
    return {
      query: `{
        localisedContent(id: "${LOCALES[ctx.locale]}") {
          feature {
            author
            title
            topic
            url
            sys {id firstPublishedAt}
            heroImage {image {url title}}
            contentfulMetadata {
              tags {
                id
                name
              }
            }
          }
          subFeaturesCollection {
            items {
              author
              title
              topic
              url
              sys {id firstPublishedAt}
              heroImage {image {url title}}
              contentfulMetadata {
                tags {
                  id
                  name
                }
              }
            }
          }
          sponsoredCollection {
            items {
              title
              topic
              url
              sys {id firstPublishedAt}
              heroImage {image {url title}}
              contentfulMetadata {
                tags {
                  id
                  name
                }
              }
            }
          }
        }
        articleCollection (
          where: {
            OR: [
              { sys: { firstPublishedAt_gt: "2021-12-26T09:24:50.304Z" } }
              { sys: { firstPublishedAt_lt: "2021-12-23T17:53:15.120Z" } }
            ]
          }
          order: sys_firstPublishedAt_DESC
        ) {
          items {
            author
            title
            topic
            url
            sys {id firstPublishedAt}
            heroImage {image {url title}}
            contentfulMetadata {
              tags {
                id
                name
              }
            }
          }
        }
      }`,
    };
  }
}

const S = {};

S.Background = styled.div`
  background: white;
  min-height: calc(100vh - 60px);
  width: 100vw;
  @media ${u.device.tablet} {
    min-height: calc(100vh - 84px);
  }
`;

S.Container = styled.div`
  margin: 0 auto;
  max-width: 1440px;
  padding: 16px;
  position: relative;
  @media ${u.device.mobile} {
    padding: 40px;
  }
`;

S.Logo = styled.article`
  cursor: default;
  margin: 20px 0 60px;
  text-align: center;
  text-transform: uppercase;
  > a {
    display: block;
  }
`;

S.Image = styled.div`
  cursor: pointer;
  position: relative;
`;

S.More = styled.section`
  margin-bottom: 40px;
`;

S.DotsIcon = styled(I.ThreeDots)`
  margin-right: 10px;
`;

S.DropdownContent = styled.div`
  background: white;
  border: 1px solid ${u.colours.charcoalFaded};
  border-radius: 3px;
  box-shadow: 0 0 5px 1px ${u.colours.charcoalFaded};
  display: none;
  font-size: 1.2rem;
  left: -10px;
  line-height: 3rem;
  min-width: 170px;
  padding: 35px 20px 17.5px;
  position: absolute;
  top: -10px;
  z-index: -2;
  > div {
    margin-top: 20px;
  }
`;

S.DropdownHover = styled.div`
  align-items: center;
  cursor: default;
  display: flex;
  position: absolute;
  left: 20px;
  top: 20px;
  z-index: 3;
  > a::after {
    display: none;
  }
  :hover {
    ${S.DropdownContent} {
      display: flex;
      flex-direction: column;
    }
  }
  @media ${u.device.mobile} {
    left: 40px;
    top: 40px;
  }
`;

S.MenuItem = styled.div`
  align-items: center;
  cursor: pointer;
  display: grid;
  gap: 10px;
  grid-template-columns: 22px auto;
  grid-template-rows: 22px;
  justify-items: center;
  margin-top: 10px;
`;

S.Line = styled.hr`
  margin-top: 20px;
`;

S.SearchAndFiltersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

S.Left = styled.div`
  flex: 1;
`;

const SM = {};

SM.Explore = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 40px;
  @media ${u.device.tablet} {
    display: grid;
    gap: 20px;
  }
  @media ${u.device.desktop} {
    gap: 80px;
  }
`;

SM.Results = styled.div`
  width: 100%;
  display: grid;
  gap: 40px;
  margin-bottom: auto;
  @media ${u.device.tablet} {
    margin-top: 20px;
  }

  @media ${u.device.desktop} {
    margin-top: 20px;
    grid-template-columns: 1fr 1fr;
  }
`;

const TT = {};

TT.H1S = styled(T.H2Rox)`
  font-size: 9.4rem;
  font-weight: 300;
  line-height: 8.8rem;
  letter-spacing: 2rem;
`;

TT.H3 = styled(T.H3)`
  font-size: 3.2rem;
  line-height: 4rem;
  margin-bottom: 20px;
`;

TT.H5 = styled(T.H5)`
  letter-spacing: 0.3rem;
`;

TT.CXL = styled(T.CXL)`
  cursor: pointer;
`;

TT.CL = styled(T.CL)`
  color: ${u.colours.charcoal};
  cursor: pointer;
  margin: 10px 0 20px;
  ${(p) =>
    p.isFeature &&
    `
    font-size: 2.4rem;
    line-height: 3.6rem;
    margin-right: 16px;
  `}
  ${(p) =>
    p.isMostRecent &&
    `
    @media (min-width: 1160px) {
      font-size: 2.4rem;
      line-height: 3.6rem;
    }
  `}
  ${(p) =>
    p.isMore &&
    !p.isLong &&
    `
    @media (min-width: 900px) {
      font-size: 2.4rem;
      line-height: 3.6rem;
    }
  `}
`;

TT.CM = styled(T.CM)`
  opacity: 1;
`;

TT.CMB = styled(TT.CM)`
  cursor: pointer;
  font-weight: bold;
`;

TT.CS = styled(T.CS)`
  margin-right: 10px;
  opacity: 1;
  text-transform: uppercase;
  ${(p) => p.nowrap && "white-space: nowrap;"}
  @media ${u.device.desktop} {
    margin-right: 20px;
  }
`;

TT.CSB = styled(TT.CS)`
  cursor: pointer;
  font-weight: bold;
  letter-spacing: 0.1rem;
`;

TT.Author = styled.span`
  cursor: pointer;
`;

TT.Em = styled.em`
  margin-left: -1rem;
`;

TT.A = styled(T.Animation)`
  font-weight: 500;
  font-size: 1.4rem;
  line-height: 2rem;
  margin: 0;
  margin-right: auto;
  ${(p) => p.isTop && "display: none;"}
  @media ${u.device.mobile} {
    display: initial;
    font-size: 1.4rem;
  }
`;
